export const DISPLAY_MAX_EDGE = 2560
export const DISPLAY_QUALITY = 0.9
export const THUMB_MAX_EDGE = 400
export const THUMB_QUALITY = 0.8

export type ResizedImage = { blob: Blob; width: number; height: number }

/**
 * Downscale an image blob to a JPEG capped at `maxEdge` on its longest side.
 * Never upscales. `from-image` applies the EXIF orientation so the output
 * pixels are already upright.
 */
export async function downscaleImage(
  source: Blob,
  opts: { maxEdge: number; quality: number },
): Promise<ResizedImage> {
  const bitmap = await createImageBitmap(source, {
    imageOrientation: 'from-image',
  })
  try {
    const scale = Math.min(
      1,
      opts.maxEdge / Math.max(bitmap.width, bitmap.height),
    )
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Canvas 2D context unavailable')
    context.drawImage(bitmap, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', opts.quality),
    )
    if (!blob) throw new Error('Canvas JPEG encoding failed')
    return { blob, width, height }
  } finally {
    bitmap.close()
  }
}

/** Both upload renditions: full-quality display + small gallery thumb. */
export async function makePhotoRenditions(source: Blob): Promise<{
  display: ResizedImage
  thumb: ResizedImage
}> {
  const display = await downscaleImage(source, {
    maxEdge: DISPLAY_MAX_EDGE,
    quality: DISPLAY_QUALITY,
  })
  // Derive the thumb from the display rendition: decoding the multi-megapixel
  // original twice doubles main-thread bitmap work for no quality gain, and
  // the display edge is 6.4× the thumb's. Orientation is already applied.
  const thumb = await downscaleImage(display.blob, {
    maxEdge: THUMB_MAX_EDGE,
    quality: THUMB_QUALITY,
  })
  return { display, thumb }
}
